import useHttp from '../helpers/HttpMethods';
import {
  TaskTemplateItem,
  TaskTemplateItemBulkInput,
  TaskTemplateItemInput,
  TaskTemplateItemOrderInput,
  TaskTemplateItems,
  TaskTemplateItemUpdateInput
} from '../types/TaskTemplateItemTypes';

class TaskTemplateItemService {
  /**
   * Create a new task template item.
   * @param {number} taskTemplateId - Task template ID.
   * @returns {Promise<Response | TaskTemplateItems>}
   */
  static async listItems(
    taskTemplateId: number
  ): Promise<Response | TaskTemplateItems> {
    return await useHttp().get(`/task-templates/${taskTemplateId}/items`);
  }

  /**
   * Create a new task template item.
   * @param {number} taskTemplateId - Task template ID.
   * @param {TaskTemplateItemInput} body - Task template item data.
   * @returns {Promise<Response | TaskTemplateItem>}
   */
  static async create(
    taskTemplateId: number,
    body: TaskTemplateItemInput
  ): Promise<Response | TaskTemplateItem> {
    return await useHttp().post(
      body,
      `/task-templates/${taskTemplateId}/items`
    );
  }

  /**
   * Update a task template item.
   * @param {number} taskTemplateId - Task template ID.
   * @param {number} taskTemplateItemId - Task template item ID.
   * @param {TaskTemplateItemUpdateInput} body - Task template item update data.
   * @returns {Promise<Response | TaskTemplateItem>}
   */
  static async update(
    taskTemplateId: number,
    taskTemplateItemId: number,
    body: TaskTemplateItemUpdateInput
  ): Promise<Response | TaskTemplateItem> {
    return await useHttp().put(
      body,
      `/task-templates/${taskTemplateId}/items/${taskTemplateItemId}`
    );
  }

  /**
   * Delete a task template item.
   * @param {number} taskTemplateId - Task template ID.
   * @param {number} taskTemplateItemId - Task template item ID.
   * @returns {Promise<Response>}
   */
  static async delete(
    taskTemplateId: number,
    taskTemplateItemId: number
  ): Promise<Response> {
    return await useHttp().del(
      `/task-templates/${taskTemplateId}/items/${taskTemplateItemId}`
    );
  }

  /**
   * Reorder a task template item.
   * @param {number} taskTemplateId - Task template ID.
   * @param {number} taskTemplateItemId - Task template item ID.
   * @param {TaskTemplateItemOrderInput} body - Order data with taskAboveId and taskBelowId.
   * @returns {Promise<Response>}
   */
  static async reorder(
    taskTemplateId: number,
    taskTemplateItemId: number,
    body: TaskTemplateItemOrderInput
  ): Promise<Response> {
    return await useHttp().post(
      body,
      `/task-templates/${taskTemplateId}/items/${taskTemplateItemId}/order`
    );
  }

  /**
   * Bulk update task template items.
   * @param {number} taskTemplateId - Task template ID.
   * @param {TaskTemplateItemBulkInput} body - Bulk update data.
   * @returns {Promise<Response>}
   */
  static async bulk(
    taskTemplateId: number,
    body: TaskTemplateItemBulkInput
  ): Promise<Response> {
    return await useHttp().put(
      body,
      `/task-templates/${taskTemplateId}/items/bulk`
    );
  }
}

export default TaskTemplateItemService;
